import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosResponse, HttpStatusCode } from "axios";
import { ApiUrlUtil } from "app/utils/apiUrlUtil";
import { HeadersUtil } from "app/utils/headersUtil";
import { ResponseApi, ResponseResultCode } from "app/model/response-api";
import { WeatherModel } from "app/model/common/weatherModel";

interface WeatherGroup {
  today: WeatherModel[];
  tomorrow: WeatherModel[];
}

interface ApiWeatherModel {
  GJ?: WeatherGroup;
  YS?: WeatherGroup;
  [key: string]: WeatherGroup | undefined;
}

interface StateModel {
  today: WeatherModel[];
  tomorrow: WeatherModel[];
}

function pickWeatherGroup(data: ApiWeatherModel | StateModel | null): WeatherGroup | null {
  if (!data) return null;

  if (Array.isArray((data as StateModel).today)) {
    return {
      today: (data as StateModel).today,
      tomorrow: (data as StateModel).tomorrow ?? [],
    };
  }

  const regional = data as ApiWeatherModel;
  if (regional.YS?.today?.length) return regional.YS;
  if (regional.GJ?.today?.length) return regional.GJ;

  const firstGroup = Object.values(regional).find(
    (group): group is WeatherGroup => !!group && Array.isArray(group.today) && group.today.length > 0
  );
  return firstGroup ?? null;
}

const initialState: StateModel = {
  today: [],
  tomorrow: [],
};

export const getWeather = createAsyncThunk("weather/weathers", async () => {
  const url = process.env.REACT_APP_URL_CO + '/weather';
  const axiosResponse: AxiosResponse = await axios.get(ApiUrlUtil.buildQueryString(url), {
    headers: HeadersUtil.getHeadersAuth(),
  });

  if (axiosResponse?.status === HttpStatusCode.Ok) {
    const apiResponse: ResponseApi<ApiWeatherModel | StateModel> = axiosResponse.data;
    if (apiResponse?.resultCode === ResponseResultCode.Y) {
      return apiResponse.data;
    }
  }
  return null;
});

const weatherSlice = createSlice({
  name: 'weather',
  initialState,
  reducers: {
    setWeather: (state, action) => {
      state.today = action.payload.today;
      state.tomorrow = action.payload.tomorrow;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWeather.pending, () => {})
      .addCase(getWeather.fulfilled, (state, action) => {
        const group = pickWeatherGroup(action.payload);
        if (group) {
          state.today = group.today ?? [];
          state.tomorrow = group.tomorrow ?? [];
        }
      })
      .addCase(getWeather.rejected, (state) => {
        state.today = [];
        state.tomorrow = [];
      });
  },
});

export default weatherSlice.reducer;